import { rowToPayment } from './mappers';
import {
  branchesMatch,
  getReceptionistBranchFilter,
  isReceptionistPanelScope,
} from '../gym/branchContext';
import { parseSalePeriodFromNote } from '../../pages/receptionist/sales/salePeriodUtils';
import { durationFromSalePackage } from '../../pages/receptionist/sales/saleDurationUtils';

function toMembershipLocalDay(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
}

/** Attach the best payment to memberships.start_date rows (Detailed Report New Memberships). */
export function attachMembershipSalesFromRows(day, membershipRows = [], payRows = [], {
  applyReceptionistBranch = true,
} = {}) {
  const reportDay = String(day || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(reportDay)) return [];

  const scopedMemberships = (membershipRows || []).filter(
    (row) => toMembershipLocalDay(row.start_date) === reportDay,
  );
  const paymentsByMember = new Map();
  (payRows || []).forEach((pay) => {
    if (!pay.member_id) return;
    const list = paymentsByMember.get(pay.member_id) || [];
    list.push(pay);
    paymentsByMember.set(pay.member_id, list);
  });

  const pickPayment = (membership) => {
    const list = paymentsByMember.get(membership.member_id) || [];
    if (!list.length) return null;
    const pkg = String(membership.package_name || '').trim().toLowerCase();
    const memStartRaw = String(membership.start_date || '').trim();
    const memStart = /^\d{4}-\d{2}-\d{2}/.test(memStartRaw)
      ? memStartRaw.slice(0, 10)
      : toMembershipLocalDay(memStartRaw);
    const samePkg = list.filter((p) => String(p.package_name || '').trim().toLowerCase() === pkg);
    const notRemaining = (p) => {
      const src = String(p.source || '').toLowerCase().replace(/-/g, '_');
      return src !== 'remaining_clear' && src !== 'remaining' && src !== 'balance_payment';
    };
    const pool = (samePkg.length ? samePkg : list).filter(notRemaining);
    if (!pool.length) return null;
    const scored = pool.map((p) => {
      const paid = toMembershipLocalDay(p.paid_at);
      const startRaw = String(p.start_date || '').trim();
      const start = /^\d{4}-\d{2}-\d{2}/.test(startRaw)
        ? startRaw.slice(0, 10)
        : toMembershipLocalDay(startRaw);
      let score = 0;
      if (memStart && start === memStart) score += 100;
      if (memStart && paid === memStart) score += 80;
      if (paid === reportDay) score += 40;
      if (start === reportDay) score += 20;
      if (String(p.source || '').toLowerCase() === 'subscription') score += 10;
      if (String(p.source || '').toLowerCase() === 'renewal_sale') score += 5;
      const headerAmt = Number(p.amount) || 0;
      score += Math.min(5, headerAmt > 0 ? 3 : 0);
      const splitSum = Array.isArray(p.payment_splits)
        ? p.payment_splits.reduce((s, x) => s + (Number(x.amount) || 0), 0)
        : 0;
      if (splitSum > 0) score += 3;
      const status = String(p.status || '').toLowerCase();
      if (status === 'completed' || status === 'paid' || status === 'success') score += 25;
      if (status === 'pending' || status === 'unpaid' || status === 'overdue') score -= 50;
      if (headerAmt <= 0 && splitSum > 0) score -= 15;
      return { p, score };
    });
    scored.sort((a, b) => b.score - a.score);
    return scored[0]?.p || null;
  };

  const sales = scopedMemberships.map((row) => {
    const embedded = row.members
      ? {
          ...row.members,
          baseGymId: row.members.gym_member_id,
        }
      : null;
    const pay = pickPayment(row);
    const serial = row.serial_no != null && row.serial_no !== ''
      ? row.serial_no
      : (embedded?.gym_member_id || '');
    const serialText = serial != null && serial !== '' ? String(serial).trim() : '';

    if (pay) {
      const payment = rowToPayment(pay, embedded);
      const source = String(pay.source || payment.source || 'subscription').toLowerCase().replace(/-/g, '_');
      const paidAt = pay.paid_at || payment.date || '';
      const collectedDate = toMembershipLocalDay(paidAt);
      const fromNote = parseSalePeriodFromNote(pay.payment_note || payment.paymentNote || '');
      const fromPackage = durationFromSalePackage(
        payment.package || row.package_name || pay.package_name || '',
      );
      return {
        ...payment,
        date: reportDay,
        paidAt,
        collectedDate,
        startDate: row.start_date || payment.startDate || fromNote?.startDate || reportDay,
        endDate: row.expiry_date || payment.endDate || fromNote?.endDate || '',
        createdAt: row.created_at || payment.createdAt || '',
        source: source === 'renewal_sale' || source === 'renewal' ? 'renewal_sale' : 'subscription',
        gymId: serialText || payment.gymId || '—',
        serialNo: serialText || payment.serialNo || '',
        duration: row.duration || payment.duration || fromNote?.durationLabel || fromPackage || '',
        package: payment.package || row.package_name || '—',
        member: payment.member || embedded?.full_name || '—',
        trainer: embedded?.trainer || payment.trainer || '',
        branch: embedded?.branch || pay.branch_name || pay.branch || '',
        complimentary: Boolean(row.complimentary) || payment.status === 'complimentary',
        membershipId: row.id,
        fromMembershipDay: true,
        paymentNote: payment.paymentNote || pay.payment_note || '',
      };
    }

    const complimentary = Boolean(row.complimentary);
    return {
      id: `mem-${row.id}`,
      rawId: `mem-${row.id}`,
      member: embedded?.full_name || '—',
      gymId: serialText || '—',
      serialNo: serialText,
      package: row.package_name || '—',
      amount: 0,
      packagePrice: 0,
      method: complimentary ? 'Complimentary' : 'cash',
      paymentSplits: [],
      date: reportDay,
      startDate: row.start_date || reportDay,
      endDate: row.expiry_date || '',
      status: complimentary ? 'complimentary' : 'completed',
      source: 'subscription',
      complimentary,
      duration: row.duration || '',
      memberId: row.member_id,
      createdAt: row.created_at || '',
      trainer: embedded?.trainer || '',
      branch: embedded?.branch || '',
      membershipId: row.id,
      fromMembershipDay: true,
    };
  });

  if (!applyReceptionistBranch || !isReceptionistPanelScope()) return sales;
  const branch = getReceptionistBranchFilter();
  return sales.filter((p) => (
    !p.memberId
    || branchesMatch(p.branch, branch)
    || !branch
  ));
}
